import React, { useState, useCallback } from 'react';
import { X, Upload, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { siteVideoService, VideoUploadProgress } from '../../../services/siteVideoService';

interface SiteVideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageKey: string;
  pageName: string;
  existingVideo?: {
    url: string;
    file_size?: number;
    duration?: number;
  };
  onUploadComplete: () => void;
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export const SiteVideoUploadModal: React.FC<SiteVideoUploadModalProps> = ({
  isOpen,
  onClose,
  pageKey,
  pageName,
  existingVideo,
  onUploadComplete,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState<VideoUploadProgress | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setUploadStatus('idle');
    setUploadProgress(null);
    setStatusMessage('');
    setErrorMessage('');
    setIsDragging(false);
    onClose();
  }, [onClose]);

  // Handle file selection
  const handleFileSelect = (file: File) => {
    // Validate file
    const validation = siteVideoService.validateVideoFile(file);
    if (!validation.valid) {
      setErrorMessage(validation.error || 'Invalid file');
      return;
    }

    setSelectedFile(file);
    setErrorMessage('');
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadStatus('uploading');
    setErrorMessage('');

    try {
      await siteVideoService.uploadVideoPresigned(
        {
          pageKey,
          pageName,
          file: selectedFile,
        },
        (progress) => {
          setUploadProgress(progress);
        },
        (status) => {
          setStatusMessage(status);
        }
      );

      setUploadStatus('success');
      setStatusMessage('Upload complete!');

      // Wait a moment before closing and refreshing
      setTimeout(() => {
        onUploadComplete();
        handleClose();
      }, 1500);

    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setErrorMessage(error.message || 'Upload failed');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-2xl border border-gray-800 max-w-2xl w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <div>
            <h2 className="text-xl font-semibold text-white">Upload Video</h2>
            <p className="text-sm text-gray-400 mt-1">{pageName}</p>
          </div>
          <button
            onClick={handleClose}
            disabled={uploadStatus === 'uploading'}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Existing video info */}
          {existingVideo && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <p className="text-sm text-blue-400">
                A video is already uploaded for this page. Uploading a new video will replace the existing one.
              </p>
              {existingVideo.file_size && (
                <p className="text-xs text-blue-300 mt-2">
                  Current file size: {siteVideoService.formatFileSize(existingVideo.file_size)}
                  {existingVideo.duration && ` • Duration: ${siteVideoService.formatDuration(existingVideo.duration)}`}
                </p>
              )}
            </div>
          )}

          {/* File drop zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              border-2 border-dashed rounded-xl p-8 text-center transition-all
              ${isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 bg-gray-800/50'}
              ${uploadStatus === 'uploading' ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:border-gray-600'}
            `}
            onClick={() => {
              if (uploadStatus !== 'uploading') {
                document.getElementById('video-file-input')?.click();
              }
            }}
          >
            <input
              id="video-file-input"
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={uploadStatus === 'uploading'}
            />

            {!selectedFile ? (
              <>
                <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-white font-medium mb-2">
                  Drop video file here or click to browse
                </p>
                <p className="text-sm text-gray-400">
                  Supported: MP4, WebM, MOV, AVI • Max size: 2GB
                </p>
              </>
            ) : (
              <>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-white font-medium mb-2">{selectedFile.name}</p>
                <p className="text-sm text-gray-400">
                  {siteVideoService.formatFileSize(selectedFile.size)}
                </p>
              </>
            )}
          </div>

          {/* Upload progress */}
          {uploadStatus === 'uploading' && uploadProgress && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">{statusMessage}</span>
                <span className="text-white font-medium">{uploadProgress.percentage}%</span>
              </div>
              <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-300 rounded-full"
                  style={{ width: `${uploadProgress.percentage}%` }}
                />
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
                <Loader className="w-4 h-4 animate-spin" />
                <span>
                  {siteVideoService.formatFileSize(uploadProgress.loaded)} / {siteVideoService.formatFileSize(uploadProgress.total)}
                </span>
              </div>
            </div>
          )}

          {/* Success message */}
          {uploadStatus === 'success' && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <p className="text-green-400">{statusMessage}</p>
            </div>
          )}

          {/* Error message */}
          {errorMessage && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-400">{errorMessage}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-800">
          <button
            onClick={handleClose}
            disabled={uploadStatus === 'uploading'}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            {uploadStatus === 'success' ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploadStatus === 'uploading' || uploadStatus === 'success'}
            className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploadStatus === 'uploading' ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};
